import { ImgID } from "../../image_loader/img_id.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { TournamentService } from "../../tournament/tournament_service.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UICanvasRect } from "../common_ui_elements/ui_canvas_rect.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { PlayDialogDifficulty } from "../dialog/play_dialog.js";
import { AnchoredTooltip } from "./anchored_tooltip.js";

/** The tournament "i" button: how many crowns a win of each difficulty gives. */
export class TournamentInfoTooltip
{
  private static readonly OWNER = "tournament_info";

  // Mock: 695 x 449 with the tail, apex 85 from the left edge, tip on the button's ring.
  private static readonly W = 695;
  private static readonly H = 405;
  private static readonly TAIL_X = 85;
  private static readonly TAIL_SCALE = 1;
  private static readonly OFFSET_Y = -16;
  private static readonly PAD_X = 50;

  private static readonly TITLE_Y = 35;
  private static readonly TITLE_W = 540;
  private static readonly TITLE_H = 98;
  private static readonly TITLE_FONT = 40;

  private static readonly DIVIDER_Y = 137;
  private static readonly DIVIDER_W = 500;
  private static readonly DIVIDER_H = 9;

  private static readonly ROW_FIRST_CENTER_Y = 193;
  private static readonly ROW_STEP = 62;
  private static readonly ROW_H = 56;
  private static readonly ROW_FONT = 37;
  private static readonly LABEL_W = 430;
  /** Digits share one column and centre in it, so "1" lines up with "2" and "3". */
  private static readonly COUNT_CENTER_X = 574;
  /** "1" has a wide left bearing, so centred by advance it looks shifted right. */
  private static readonly ONE_NUDGE_X = -5;
  private static readonly COUNT_W = 60;
  private static readonly COUNT_FONT = 43;
  private static readonly CROWN_CENTER_X = 630;
  private static readonly CROWN_SIZE = 54;
  private static readonly OUTLINE = 4;

  private static readonly ROWS: readonly { key: string; difficulty: PlayDialogDifficulty }[] = [
    { key: "TOURNAMENT.INFO.COMMON", difficulty: PlayDialogDifficulty.Common },
    { key: "TOURNAMENT.INFO.HARD", difficulty: PlayDialogDifficulty.Hard },
    { key: "TOURNAMENT.INFO.SUPER_HARD", difficulty: PlayDialogDifficulty.SuperHard },
  ];

  public static show( anchor: UIElement ): void
  {
    AnchoredTooltip.show( anchor, {
      text: "",
      anchor_touch_closes: true,
      custom: {
        design_w: TournamentInfoTooltip.W,
        design_h: TournamentInfoTooltip.H,
        tail_x: TournamentInfoTooltip.TAIL_X,
        tail_scale: TournamentInfoTooltip.TAIL_SCALE,
        offset_y: TournamentInfoTooltip.OFFSET_Y,
        build: ( ctx, substrate, w ) => TournamentInfoTooltip.build( ctx, substrate, w ),
      },
    }, TournamentInfoTooltip.OWNER );
  }

  private static build( ctx: CanvasRenderingContext2D, substrate: UIElement, w: number ): void
  {
    const T = TournamentInfoTooltip;
    const pad_x = LayoutScaler.widthFromDesign( T.PAD_X );

    const title_w = LayoutScaler.widthFromDesign( T.TITLE_W );
    substrate.addChild( T.text( ctx, { localization_key: "TOURNAMENT.INFO.TITLE" }, ( w - title_w ) / 2, LayoutScaler.yFromDesign( T.TITLE_Y ),
      title_w, LayoutScaler.heightFromDesign( T.TITLE_H ), T.TITLE_FONT, MyTextAlign.Center, true ), true );

    const divider_w = LayoutScaler.widthFromDesign( T.DIVIDER_W );
    const divider_h = LayoutScaler.heightFromDesign( T.DIVIDER_H );
    substrate.addChild( new UICanvasRect( ctx, ( w - divider_w ) / 2, LayoutScaler.yFromDesign( T.DIVIDER_Y ),
      divider_w, divider_h, ColorScheme.anchored_tooltip_divider, divider_h / 2 ), true );

    const row_h = LayoutScaler.heightFromDesign( T.ROW_H );
    const crown = LayoutScaler.widthFromDesign( T.CROWN_SIZE );
    const label_w = LayoutScaler.widthFromDesign( T.LABEL_W );
    const label_font = T.sharedLabelFont( ctx, label_w );
    T.ROWS.forEach( ( row, i ) =>
    {
      const center_y = LayoutScaler.yFromDesign( T.ROW_FIRST_CENTER_Y + T.ROW_STEP * i );
      const y = center_y - row_h / 2;

      substrate.addChild( T.text( ctx, { localization_key: row.key }, pad_x, y, label_w, row_h,
        T.ROW_FONT, MyTextAlign.Left, false, label_font ), true );

      const count = String( TournamentService.crownsForWin( row.difficulty ) );
      const count_x = T.COUNT_CENTER_X - T.COUNT_W / 2 + ( count === "1" ? T.ONE_NUDGE_X : 0 );
      substrate.addChild( T.text( ctx, { text: count }, LayoutScaler.xFromDesign( count_x ), y,
        LayoutScaler.widthFromDesign( T.COUNT_W ), row_h, T.COUNT_FONT, MyTextAlign.Center, false ), true );

      substrate.addChild( new UIImage( ctx, LayoutScaler.xFromDesign( T.CROWN_CENTER_X ) - crown / 2, center_y - crown / 2,
        crown, LayoutScaler.heightFromDesign( T.CROWN_SIZE ), ImgID.UI_TournamentMiniCrown ), true );
    } );
  }

  /**
   * One font for all difficulty labels: the largest that fits the longest of them, so a
   * longer label does not end up smaller than the others (each fitting on its own did).
   */
  private static sharedLabelFont( ctx: CanvasRenderingContext2D, label_w: number ): number
  {
    const T = TournamentInfoTooltip;
    const max = LayoutScaler.textSizeFromDesign( T.ROW_FONT );
    const min = LayoutScaler.textSizeFromDesign( Math.round( T.ROW_FONT * 0.6 ) );
    const outline = LayoutScaler.textBorderSizeFromDesign( T.OUTLINE );
    ctx.save();
    ctx.font = `normal 900 ${max}px ${TextStyleScheme.geologica}`;
    const widest = Math.max( ...T.ROWS.map( row => ctx.measureText( LocalizationManager.t( row.key ) ).width ) );
    ctx.restore();
    if ( widest <= 0 ) return max;
    return Math.max( min, Math.min( max, Math.floor( max * ( label_w - outline * 2 ) / widest ) ) );
  }

  private static text(
    ctx: CanvasRenderingContext2D,
    content: { localization_key: string } | { text: string },
    x: number,
    y: number,
    w: number,
    h: number,
    font_size: number,
    text_align: MyTextAlign,
    auto_wrap: boolean,
    /** Already in pixels and shared with sibling rows: no per-row fitting. */
    fixed_px?: number
  ): UITextElement
  {
    return new UITextElement( ctx, x, y, w, h, {
      ...content,
      font_size: fixed_px ?? LayoutScaler.textSizeFromDesign( font_size ),
      min_font_size: LayoutScaler.textSizeFromDesign( Math.round( font_size * 0.6 ) ),
      font_weight: 900,
      text_color: ColorScheme.anchored_tooltip_text,
      text_style: TextStyleScheme.geologica,
      text_align,
      vertical_align: MyTextVerticalAlign.Center,
      // By glyph bounds "р"/"й" would shift a row, so all rows and digits centre on the cap height.
      center_on_cap_height: true,
      outline_width: LayoutScaler.textBorderSizeFromDesign( TournamentInfoTooltip.OUTLINE ),
      outline_color: ColorScheme.white,
      fit_font_size: fixed_px === undefined,
      line_height: 1,
      auto_wrap,
    } );
  }
}
